import { useEffect, useState } from "react";

import { AIAgentSessionHelper } from "@/helpers/agentcore/ai-agent-session-helper";

/** What the agent is doing, as the surfaces that report it need to read it. */
export interface AgentProgress {
  /** The agent's latest message, or undefined until it has sent one. */
  readonly message: string | undefined;
  /** Whether the agent has handed back and is waiting on the hire. */
  readonly isUserTurn: boolean;
  /** Whether a turn is in flight, which is what the typing indicator reports. */
  readonly isProcessing: boolean;
}

/**
 * The agent's progress, for a surface that reports it.
 *
 * Each caller subscribes for itself: the banner and the step's indicator are mounted apart, and the
 * host's listener is additive, which two callers here already rely on.
 *
 * @param isListening False stops listening and reports nothing in flight.
 */
export const useAgentProgress = (isListening: boolean): AgentProgress => {
  const [message, setMessage] = useState<string | undefined>(undefined);
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [isProcessing, setIsProcessing] = useState(isListening);

  useEffect(() => {
    if (!isListening) {
      /*
       * The message goes too. A long-lived caller stays mounted between runs, and keeping the last
       * one would open the next run on the previous run's status line.
       */
      setMessage(undefined);
      setIsUserTurn(false);
      setIsProcessing(false);
      return;
    }
    setIsProcessing(true);

    const cleanup = window.electronAPI?.onAgentActionProgress?.((result) => {
      if (!result.success || !result.response) {
        return;
      }
      const { message: nextMessage, state } = result.response;
      if (nextMessage) {
        setMessage(nextMessage);
      }
      setIsUserTurn(state === "PENDING_USER_ACTION");
      /* One list of terminal states, in the helper, so this cannot disagree with it. */
      setIsProcessing(
        state !== "PENDING_USER_ACTION" &&
          !AIAgentSessionHelper.isTerminalState(result.response)
      );
    });

    return () => cleanup?.();
  }, [isListening]);

  return { isProcessing, isUserTurn, message };
};
